/**
 * Arriving: deliveries to Jurupa, late first, then by week. Each row opens the PO or container
 * with every line. A PO with late lines and later lines shows up in both places, marked.
 */
import { useState } from "react";
import { addDays, daysBetween, fmt, refLabel, shortDate, useSupply, weekStart, type Delivery, type Line } from "../lib/supply.ts";
import { usePanel } from "../components/DetailPanel.tsx";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import { ExportButton, ListBar } from "../components/ListBar.tsx";
import { downloadCsv } from "../lib/csv.ts";
import { partName } from "../lib/skuNames.ts";

const RANGES = ["overdue", "2w", "4w", "nodate"] as const;
const RANGE_LABEL: Record<Range, string> = { overdue: "Overdue", "2w": "Next 2 weeks", "4w": "Next 4 weeks", nodate: "No date" };

type Range = "overdue" | "2w" | "4w" | "nodate";

export function ArrivingPage() {
  const s = useSupply();
  const { open } = usePanel();
  const [range, setRange] = useState<Range>("overdue");
  const [filter, setFilter] = useState("");
  if (s.error) return <div className="u-error">Couldn't load deliveries: {s.error.message}</div>;

  const today = s.today;
  const wk0 = weekStart(today);
  const windowsFor = (weeks: number) => Array.from({ length: weeks }, (_, i) => {
    const from = i === 0 ? today : addDays(wk0, 7 * i);
    const to = addDays(wk0, 7 * i + 6);
    return { label: i === 0 ? "This week" : i === 1 ? "Next week" : `Week of ${shortDate(from)}`, from, to };
  });
  const windows = windowsFor(range === "4w" ? 4 : 2);
  // The filter matches a PO / container number or any part on it (code or name).
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (d: Delivery) => !words.length || words.every((w) => `${d.ref} ${d.lines.map((l) => `${l.sku} ${partName(l.sku)}`).join(" ")}`.toLowerCase().includes(w));
  const all = [...s.deliveries.values()].filter(hit);
  const late = s.late.filter(hit);
  const stalled = s.stalled.filter(hit);
  const lateRefs = new Set(late.map((d) => d.ref));
  const inWindow = (d: Delivery, from: string, to: string) => d.later.filter((l) => l.date && l.date >= from && l.date <= to);
  const noDate = all.filter((d) => d.lines.some((l) => !l.date));
  const upTo = (weeks: number) => {
    const ws = windowsFor(weeks);
    return all.filter((d) => ws.some((w) => inWindow(d, w.from, w.to).length)).length;
  };
  const counts: Record<Range, number> = { overdue: stalled.length + late.length, "2w": upTo(2), "4w": upTo(4), nodate: noDate.length };
  const exportRows = () =>
    downloadCsv(`arriving-${range}-${today}.csv`, (range === "nodate" ? noDate : range === "overdue" ? [...stalled, ...late] : all.filter((d) => windows.some((w) => inWindow(d, w.from, w.to).length))).flatMap((d) =>
      d.lines.map((l) => ({
        "PO or container": refLabel(d.ref, d.kind),
        Part: l.sku,
        Name: partName(l.sku),
        Qty: l.qty,
        Unit: l.yards ? "yd" : "units",
        Due: l.date ?? "",
        Late: l.date && l.date < today ? "yes" : "",
      }))));

  const go = (d: Delivery) => open(d.kind === "po" ? "po" : "ca", d.ref);
  const parts = (lines: Line[]) => {
    const t = lines.slice(0, 2).map((l) => `${l.sku} ×${fmt(l.qty)}`).join(" · ");
    return lines.length > 2 ? `${t} · +${lines.length - 2} more` : t;
  };
  const qty = (lines: Line[]) => {
    const u = lines.reduce((t, l) => t + l.qty, 0);
    const yd = lines.every((l) => l.yards);
    return `${fmt(u)} ${yd ? "yd" : "units"}`;
  };
  const Ref = ({ d, extra }: { d: Delivery; extra?: string }) => (
    <td className="l">
      <span className="code">{refLabel(d.ref, d.kind)}</span>
      {d.kind === "container" && <span className="sub"> container</span>}
      {extra && <span className="sub"> · {extra}</span>}
    </td>
  );

  return (
    <div className="u-page">
      <CategoryTabs items={RANGES} value={range} onChange={setRange} label={(k) => RANGE_LABEL[k]} counts={counts} loading={s.loading} />
      <p className="page-note">Deliveries to Jurupa. Open a row to see every line. Receipts in NetSuite show here within the hour.</p>
      <ListBar title={`${RANGE_LABEL[range]} · ${s.loading ? "…" : counts[range]} ${counts[range] === 1 ? "delivery" : "deliveries"}`} search={filter} onSearch={setFilter} placeholder="Filter POs or parts">
        <ExportButton onClick={exportRows} />
      </ListBar>
      {s.loading ? (
        <div className="tablewrap"><div className="u-empty">Loading…</div></div>
      ) : (
        <div className="tablewrap">
          <table className="dt left">
            <thead>
              <tr>
                <th>PO or container</th>
                <th className="l">When</th>
                <th>Qty</th>
                <th className="l hide-m">Main parts</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {range === "nodate" ? (
                <>
                  <tr className="grp"><td className="l" colSpan={5}>No date set <span className="sub">· {noDate.length}</span></td></tr>
                  {noDate.map((d) => {
                    const lines = d.lines.filter((l) => !l.date);
                    return (
                      <tr key={d.ref} className="click" onClick={() => go(d)}>
                        <Ref d={d} />
                        <td className="l muted">no date</td>
                        <td>{qty(lines)}</td>
                        <td className="l hide-m parts"><span className="sub">{parts(lines)}</span></td>
                        <td className="chev">›</td>
                      </tr>
                    );
                  })}
                </>
              ) : range === "overdue" ? (
                <>
                  {stalled.length + late.length === 0 && <tr><td className="l muted" colSpan={5}>Nothing overdue.</td></tr>}
                  {stalled.length > 0 && (
                    <tr className="grp" id="stalled">
                      <td className="l" colSpan={5}>
                        Stalled <span className="sub">· {stalled.length} · over 30 days late and never shipped, so they're the most overdue. Chase the supplier, or close the line in NetSuite.</span>
                      </td>
                    </tr>
                  )}
                  {stalled.map((d) => (
                    <tr key={`st-${d.ref}`} className="click" onClick={() => go(d)}>
                      <Ref d={d} />
                      <td className="l">
                        due {shortDate(d.stalled[0].date)}
                        {d.stalled[0].date && <><span className="hide-m"> · </span><span className="late-t">{daysBetween(d.stalled[0].date, today)} days late</span></>}
                      </td>
                      <td>{qty(d.stalled)}</td>
                      <td className="l hide-m parts"><span className="sub">{parts(d.stalled)}</span></td>
                      <td className="chev">›</td>
                    </tr>
                  ))}
                  <tr className="grp"><td className="l" colSpan={5}>Late <span className="sub">· {late.length} · due date passed, under 30 days</span></td></tr>
                  {late.map((d) => (
                    <tr key={d.ref} className="click" onClick={() => go(d)}>
                      <Ref d={d} />
                      <td className="l">
                        <span className="hide-m">{d.late.length > 1 && d.kind === "po" ? "oldest line due " : "due "}</span>
                        {shortDate(d.oldestLate)}
                        <span className="hide-m"> · </span><span className="late-t">{d.daysLate} days late</span>
                      </td>
                      <td>
                        {d.late.length > 1 && <span className="hide-m">{d.late.length} lines · </span>}
                        {qty(d.late)}
                      </td>
                      <td className="l hide-m parts"><span className="sub">{parts(d.late)}</span></td>
                      <td className="chev">›</td>
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  {windows.map((w) => {
                    const rows = all
                      .map((d) => ({ d, lines: inWindow(d, w.from, w.to) }))
                      .filter((x) => x.lines.length)
                      .sort((a, b) => a.lines[0].date!.localeCompare(b.lines[0].date!));
                    return (
                      <WeekRows key={w.label}>
                        <tr className="grp">
                          <td className="l" colSpan={5}>
                            {w.label} <span className="sub">· {shortDate(w.from)} – {shortDate(w.to)} · {rows.length}</span>
                          </td>
                        </tr>
                        {rows.length === 0 && <tr><td className="l muted" colSpan={5}>Nothing due.</td></tr>}
                        {rows.map(({ d, lines }) => (
                          <tr key={d.ref} className="click" onClick={() => go(d)}>
                            <Ref d={d} extra={lateRefs.has(d.ref) ? "later line" : undefined} />
                            <td className="l">
                              {d.kind === "container" ? "about " : ""}
                              {shortDate(lines[0].date)}
                              {daysBetween(today, lines[0].date!) === 0 && <span className="sub"> · today</span>}
                            </td>
                            <td>{qty(lines)}</td>
                            <td className="l hide-m parts"><span className="sub">{parts(lines)}</span></td>
                            <td className="chev">›</td>
                          </tr>
                        ))}
                      </WeekRows>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="fine">Container dates are the ship's arrival + 9 days to reach Jurupa. Only parts this app tracks are counted.</p>
    </div>
  );
}

function WeekRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
